import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  isPushSupported,
  getNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isPushSubscribedLocally,
  registerServiceWorker,
} from "@/lib/pushNotifications";

export default function PushOptIn() {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) return;
    registerServiceWorker();
    const permission = getNotificationPermission();
    const wasDismissed = sessionStorage.getItem("push_optin_dismissed") === "1";
    setDismissed(wasDismissed);
    setSubscribed(isPushSubscribedLocally() || permission === "granted");
    setVisible(permission === "default" && !wasDismissed && !isPushSubscribedLocally());
  }, []);

  const handleEnable = async () => {
    setLoading(true);
    try {
      await subscribeToPush();
      setSubscribed(true);
      setVisible(false);
      toast.success("Notifications activées !");
    } catch (err) {
      toast.error(err.message || "Impossible d'activer les notifications");
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    setLoading(true);
    try {
      await unsubscribeFromPush();
      setSubscribed(false);
      toast.success("Notifications désactivées");
    } catch {
      toast.error("Erreur lors de la désactivation");
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem("push_optin_dismissed", "1");
    setDismissed(true);
    setVisible(false);
  };

  if (!isPushSupported()) return null;

  if (visible && !dismissed) {
    return (
      <div className="mb-4 flex items-center gap-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3" data-testid="push-optin-banner">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-100">
          <Bell className="h-4 w-4 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900">Activer les notifications push</p>
          <p className="text-xs text-slate-500">Recevez les rappels de séances et messages importants.</p>
        </div>
        <Button size="sm" onClick={handleEnable} disabled={loading} className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white rounded-full">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Activer"}
        </Button>
        <button onClick={handleDismiss} className="shrink-0 text-slate-400 hover:text-slate-600" aria-label="Fermer">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  if (subscribed) {
    return (
      <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-white px-4 py-2.5">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Bell className="h-4 w-4 text-green-600" />
          <span>Notifications push activées</span>
        </div>
        <Button variant="ghost" size="sm" onClick={handleDisable} disabled={loading} className="text-slate-500 hover:text-slate-700">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><BellOff className="h-3.5 w-3.5 mr-1" /> Désactiver</>}
        </Button>
      </div>
    );
  }

  return null;
}